import { useEffect, useRef, useState } from 'react';
import {
  PROTOCOL_VERSION,
  SYSTEM_EVENTS,
  decode,
  type HelloPayload,
  type OverlayPresencePayload,
  type SessionConfigPayload,
} from '@efira/protocol';
import { CHANNELS } from '@efira/protocol';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Icon, Notice, Panel } from '@efira/ui';
import { io } from 'socket.io-client';

import {
  setOverlayPresence,
  setQuota,
  useConnectLive,
  useDisconnectLive,
  useOverlayPresence,
  usePreflight,
  useMe,
  useQuota,
  useCatalog,
  useSetScene,
  type LiveStatus,
} from '../api/queries';
import { apiUrl } from '../auth/client';
import { QuotaPanel } from './QuotaPanel';

/**
 * Зона «Эфир»: присутствие overlay и выбор сцены.
 *
 * Присутствие закрывает разрыв воронки конкурента: у него приложение не
 * проверяет и не подсказывает, что overlay добавлен в OBS, и самый дорогой шаг
 * воронки остаётся без обратной связи (docs/SPEC.md, 2.2 п. 2).
 *
 * Выбор сцены - проверка требования «смена без перезагрузки browser source»
 * (docs/ARCHITECTURE.md, раздел 6). Рядом показывается фактическая задержка от
 * нажатия до применения в overlay: бюджет - не более 120 мс на одном
 * континенте, и его надо видеть, а не предполагать.
 */

/**
 * Отметка времени для замера пути dashboard -> сервер -> overlay.
 *
 * Вынесена из компонента: правило react-hooks/purity считает обращение к
 * `performance` нечистым вызовом внутри тела компонента - и по существу право,
 * даже если конкретно здесь вызов идёт из обработчика.
 */
const stamp = () => performance.now();

/** Сцены-фикстуры блока 4б. В Фазе 4 список придёт из packages/games. */
/**
 * Проверочные сцены - не игры, и это видно по названию.
 *
 * Оставлены в интерфейсе намеренно: это самый быстрый способ ответить на
 * вопрос «а browser source вообще работает?» до того, как разбираться с
 * игрой, привязками и подарками. Ответ «кадр шевелится» стоит одного клика.
 */
/**
 * Уровни симулятора. Названия говорят о ЧАТЕ, а не о параметре: стример
 * выбирает, какой эфир воспроизвести, а не число событий в секунду.
 */
const SIMULATOR_LEVELS = [
  { id: 'calm', title: 'Симулятор: спокойный чат' },
  { id: 'active', title: 'Симулятор: живой чат' },
  { id: 'chaos', title: 'Симулятор: шторм' },
] as const;

const PROBE_SCENES = [
  { id: 'pulse', title: 'Пульс', hint: 'цикл кадров' },
  { id: 'ticker', title: 'Тикер', hint: 'кадры, таймер, слушатель' },
];

export function StreamZone() {
  const client = useQueryClient();
  const { data: presence } = useOverlayPresence();
  const { data: preflight } = usePreflight();
  const setScene = useSetScene();
  const catalog = useCatalog();
  const connect = useConnectLive();
  const disconnect = useDisconnectLive();

  // Статус подключения приходит ПОШАГОВО сокетом, а не одним ответом в конце.
  // У конкурента между нажатием и понятным статусом проходит около пятнадцати
  // секунд молчания (docs/research/APP_UX_MAP.md, 4.2) - это и есть самая
  // дорогая дыра его воронки.
  const [live, setLive] = useState<LiveStatus>({ status: 'idle' });

  const [socketReady, setSocketReady] = useState(false);
  const quota = useQuota();
  const me = useMe();
  const paidGames = me.data?.user?.plan.paidGames;
  const navigate = useNavigate();
  const [sceneId, setSceneId] = useState<string | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);

  // Момент нажатия - для замера полного пути dashboard -> сервер -> overlay.
  // Замер живёт в ref, а не в состоянии: он не влияет на отрисовку и не должен
  // вызывать лишний рендер между нажатием и ответом.
  const sentAt = useRef<number | null>(null);

  useEffect(() => {
    const socket = io(apiUrl(''), {
      transports: ['websocket'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      setSocketReady(true);
      socket.emit(SYSTEM_EVENTS.hello, {
        protocol: PROTOCOL_VERSION,
        role: 'dashboard',
      } satisfies HelloPayload);
    });

    socket.on('disconnect', () => {
      setSocketReady(false);
    });

    socket.on(SYSTEM_EVENTS.overlayPresence, (raw: unknown) => {
      const next = decode<OverlayPresencePayload>(raw);

      if (next !== null) {
        // Обновление кладётся в тот же кэш, откуда читает useOverlayPresence:
        // один источник правды на оба пути - запрос и сокет.
        setOverlayPresence(client, next);
      }
    });

    socket.on('live:status', (raw: unknown) => {
      const next = decode<LiveStatus>(raw);

      if (next !== null) {
        setLive(next);
      }
    });

    socket.on(CHANNELS.sessionConfig, (raw: unknown) => {
      const config = decode<SessionConfigPayload>(raw);

      if (config === null) {
        return;
      }

      setSceneId(config.sceneId);

      // Квота приезжает той же рассылкой, что и остальная статика сессии:
      // отдельный опрос расходился бы с ней молча.
      setQuota(client, {
        ...config.quota,
        localDate: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (sentAt.current !== null) {
        // Dashboard получает ту же рассылку, что и overlay: это честная
        // оценка полного пути, а не времени REST-ответа.
        setLatencyMs(Math.round(stamp() - sentAt.current));
        sentAt.current = null;
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [client]);

  const connected = presence?.connected === true;

  function choose(next: string | null) {
    sentAt.current = stamp();
    setSceneId(next);
    setScene.mutate(next);
  }

  const catalogGame = catalog.data?.games.find((game) => game.id === sceneId);
  const sceneTitle =
    catalogGame?.title['ru'] ??
    PROBE_SCENES.find((scene) => scene.id === sceneId)?.title ??
    sceneId;

  return (
    // Две колонки на широком экране: чеклист и сцена видны одновременно с
    // остатком времени - решение о выходе в эфир принимается, глядя на всё сразу.
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
      <div className="flex flex-col gap-4 lg:col-start-1">
        <Panel title="Overlay в OBS" subtitle="Состояние browser source прямо сейчас">
          <div className="flex items-start gap-3">
            <span
              className={`status status-lg mt-2 shrink-0 ${connected ? 'status-success' : ''}`}
              aria-hidden="true"
            />
            <div className="flex flex-col gap-1 text-sm">
              <strong>{connected ? 'Overlay подключён' : 'Overlay не подключён'}</strong>
              <span>
                {connected
                  ? describeConnected(presence ?? null)
                  : 'Вставьте ссылку из вкладки Overlay в источник «Браузер» — индикатор переключится сам.'}
              </span>
            </div>
          </div>

          {connected && (presence?.connections ?? 0) > 1 ? (
            <Notice tone="warning" title={`Открыто источников: ${presence?.connections ?? 0}`}>
              Обычно это забытая вкладка браузера с той же ссылкой. Лишние источники получают тот же
              поток и расходуют канал впустую.
            </Notice>
          ) : null}

          {!socketReady ? (
            <p className="text-foreground/80 text-sm">Связь с сервером восстанавливается…</p>
          ) : null}
        </Panel>

        <Panel title="Предполётный чеклист" subtitle="Всё, что стоит проверить до выхода в эфир">
          <ul className="flex flex-col gap-3">
            <li className="flex items-start gap-3">
              <CheckMark ok={preflight?.overlayOpen === true} />
              <span className="flex flex-col">
                <strong>Overlay открыт в OBS</strong>
                <em className="text-foreground/80 text-sm not-italic">
                  {preflight?.overlayOpen === true
                    ? 'источник на связи'
                    : 'вставьте ссылку из вкладки «Overlay» в источник «Браузер»'}
                </em>
              </span>
            </li>

            <li className="flex items-start gap-3">
              <CheckMark ok={preflight?.channelSelected === true} />
              <span className="flex flex-col">
                <strong>Канал выбран</strong>
                <em className="text-foreground/80 text-sm not-italic">
                  {preflight?.channelHandle === null || preflight?.channelHandle === undefined
                    ? 'добавьте канал во вкладке «Каналы»'
                    : `@${preflight.channelHandle}`}
                </em>
              </span>
            </li>

            <li className="flex items-start gap-3">
              <CheckMark ok={false} />
              <span className="flex flex-col">
                <strong>LIVE запущен в TikTok</strong>
                <em className="text-foreground/80 text-sm not-italic">
                  Это делается в приложении TikTok, а не здесь. Пока эфир не идёт, подключение
                  вернёт «канал не в эфире» — сразу, без ожидания.
                </em>
              </span>
            </li>
          </ul>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              size="m"
              loading={connect.isPending && live.status === 'connecting'}
              disabled={preflight?.sources.tiktok !== true || preflight?.channelSelected !== true}
              onClick={() => {
                connect.mutate(
                  { mode: 'tiktok' },
                  {
                    onError: (rejection) => {
                      setLive(rejection as unknown as LiveStatus);
                    },
                  },
                );
              }}
            >
              Подключиться к эфиру
            </Button>

            {/*
              Интенсивность симулятора выбирается, а не зашита.

              Сервер умеет три уровня с самого блока 5а, а кнопка запускала
              только спокойный. Разница не косметическая: на спокойном - около
              трёх событий в минуту на действие, и игра, где круг стоит сто
              двадцать толчков, в симуляторе не двигается вовсе. Стример,
              проверяющий её перед эфиром, видит неподвижный экран и решает,
              что игра сломана.

              Симулятор - лучший элемент продукта конкурента и главный ответ
              страху новичка: проверить игру без TikTok, без риска и без
              расхода лимита. Инструмент, который не может показать игру, этой
              работы не делает.
            */}
            {SIMULATOR_LEVELS.map((level) => (
              <Button
                key={level.id}
                variant="secondary"
                size="m"
                loading={connect.isPending}
                onClick={() => {
                  connect.mutate({ mode: 'synthetic', intensity: level.id });
                }}
              >
                {level.title}
              </Button>
            ))}

            {live.status === 'live' ? (
              <Button
                variant="ghost"
                size="m"
                loading={disconnect.isPending}
                onClick={() => {
                  disconnect.mutate(undefined, {
                    onSuccess: () => {
                      setLive({ status: 'idle' });
                    },
                  });
                }}
              >
                Отключиться
              </Button>
            ) : null}
          </div>

          {preflight?.sources.tiktok !== true ? (
            <Notice tone="info" title="Подключение к реальному эфиру не настроено">
              На этом сервере нет ключа источника событий. Симулятор работает — им можно настроить
              игру, привязки и раскладку.
            </Notice>
          ) : null}

          {live.status === 'connecting' ? (
            <Notice tone="info" title="Подключаемся" live>
              {live.message ?? 'Проверяем канал'}
            </Notice>
          ) : null}

          {live.status === 'failed' ? (
            <Notice tone="warning" title={live.message ?? 'Не удалось подключиться'} live>
              {live.action ?? 'Попробуйте ещё раз.'}
            </Notice>
          ) : null}

          {live.status === 'live' ? (
            <Notice
              tone="success"
              title={live.sourceId === 'synthetic' ? 'Симулятор идёт' : 'Эфир идёт'}
            >
              {live.sourceId === 'synthetic'
                ? 'События синтетические: они не расходуют квоту и не попадают в аналитику.'
                : `Канал @${live.handle ?? ''}. Минут в эфире: ${preflight?.session?.minutesSoFar ?? 0}.`}
            </Notice>
          ) : null}
        </Panel>

        <Panel
          title="Сцена в overlay"
          subtitle="Смена происходит без перезагрузки browser source — в OBS ничего не моргает"
        >
          <div className="flex flex-col gap-2">
            {(catalog.data?.games ?? [])
              .filter((game) => game.playable)
              .map((game) => {
                /**
                 * Платная игра на бесплатном тарифе показывается НЕАКТИВНОЙ С
                 * ПРИЧИНОЙ, а не прячется (правило 1 видимости,
                 * ARCHITECTURE 4.2-а). Спрятанная возможность не продаёт тариф
                 * и выглядит как её отсутствие; названная - объясняет, за что
                 * платят.
                 */
                const locked = !game.free && paidGames !== true;

                return (
                  <Button
                    key={game.id}
                    variant={sceneId === game.id ? 'primary' : 'secondary'}
                    size="m"
                    aria-pressed={sceneId === game.id}
                    disabled={locked}
                    loading={setScene.isPending && sceneId === game.id}
                    onClick={() => {
                      choose(game.id);
                    }}
                  >
                    <span className="flex flex-col items-start text-left">
                      <span className="font-semibold">
                        {game.title['ru']}
                        {game.free ? null : (
                          <Badge variant="default" className="ml-2">
                            Pro
                          </Badge>
                        )}
                      </span>
                      <span className="text-xs opacity-80">
                        {locked ? 'Входит в тариф Pro' : game.tagline['ru']}
                      </span>
                    </span>
                  </Button>
                );
              })}
          </div>

          {/* Игры, которых ещё нет, названы числом и помечены честно: обещание
              в интерфейсе, за которым ничего нет, - это дефект конкурента,
              из-за которого пользователь введён в заблуждение до оплаты. */}
          {(catalog.data?.games ?? []).some((game) => !game.playable) ? (
            <p className="text-foreground/75 text-sm">
              Ещё {(catalog.data?.games ?? []).filter((game) => !game.playable).length} игр в работе
              — появятся в этом же списке.
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-foreground/75 text-sm">Проверка оверлея:</span>
            {PROBE_SCENES.map((scene) => (
              <Button
                key={scene.id}
                variant={sceneId === scene.id ? 'primary' : 'secondary'}
                size="m"
                aria-pressed={sceneId === scene.id}
                loading={setScene.isPending && sceneId === scene.id}
                onClick={() => {
                  choose(scene.id);
                }}
              >
                {scene.title}
              </Button>
            ))}

            <Button
              variant={sceneId === null ? 'primary' : 'secondary'}
              size="m"
              aria-pressed={sceneId === null}
              onClick={() => {
                choose(null);
              }}
            >
              Пусто
            </Button>
          </div>

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-foreground/75">Сейчас в overlay</dt>
            <dd>{sceneId === null ? 'сцены нет, кадр прозрачен' : sceneTitle}</dd>
            <dt className="text-foreground/75">Задержка до применения</dt>
            <dd>
              {latencyMs === null
                ? 'переключите сцену — покажем'
                : `${latencyMs} мс (бюджет: не более 120 мс)`}
            </dd>
          </dl>

          {!connected ? (
            <p className="text-foreground/80 text-sm">
              Overlay не подключён — смена сцены сохранится и применится, как только он откроется.
            </p>
          ) : null}
        </Panel>

        <Panel title="Скоро в этой вкладке">
          <p className="text-foreground/80 max-w-prose text-sm">
            Статистика эфира и здоровье соединения: задержка событий, потери, состояние источника.
            Сцены выше — пробные, для проверки переключения; настоящие игры выходят волнами, первая
            — викторина.
          </p>
        </Panel>
      </div>

      <div className="flex flex-col gap-4 lg:col-start-2 lg:sticky lg:top-24">
        {quota.data === undefined ? null : (
          <QuotaPanel
            quota={quota.data}
            onUpgrade={() => {
              void navigate('/app/account');
            }}
          />
        )}
      </div>
    </div>
  );
}

/** Отметка пункта чеклиста: выполнен - зелёная галочка, нет - пустой круг. */
function CheckMark({ ok }: { ok: boolean }) {
  return (
    <Icon
      name={ok ? 'success' : 'circle'}
      size="lg"
      className={ok ? 'text-primary mt-1' : 'text-foreground/40 mt-1'}
    />
  );
}

function describeConnected(presence: OverlayPresencePayload | null): string {
  if (presence?.since === null || presence?.since === undefined) {
    return 'Источник на связи.';
  }

  const time = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(presence.since));

  return `На связи с ${time}. Источников: ${presence.connections}.`;
}
